import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Download, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 })
    : "—";

// Convert stored UTC ISO timestamp (e.g. "2026-07-01T17:12:25.123") to IST wall-clock time
// (e.g. "01 Jul 2026, 10:42 PM"). Falls back to raw string if parsing fails.
const fmtIST = (iso) => {
  if (!iso) return "—";
  try {
    // The backend stores naive UTC ISO (no "Z"). Force UTC parsing by appending Z when absent.
    const s = iso.endsWith("Z") || iso.match(/[+-]\d{2}:\d{2}$/) ? iso : iso + "Z";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(iso).slice(0, 19).replace("T", " ");
  }
};

export default function EngineHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    strategy_name: "",
    status: "",
    search: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v)
      );
      const r = await api.get("/admin/engine-history", { params });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error("Failed to load engine history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      );
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/engine-history.csv?${params}`;
      const tok = localStorage.getItem("pagewise_token");
      const resp = await fetch(url, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = "engine-run-history.csv";
      a.click();
    } catch {
      toast.error("Export failed");
    }
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (a, r) => ({
        runs: a.runs + 1,
        buys: a.buys + (r.buys || 0),
        sells: a.sells + (r.sells || 0),
        skipped: a.skipped + (r.skipped || 0),
        time: a.time + (r.processing_time_s || 0),
      }),
      { runs: 0, buys: 0, sells: 0, skipped: 0, time: 0 }
    );
  }, [rows]);

  const [purgeBefore, setPurgeBefore] = useState("2026-06-27");
  const [purging, setPurging] = useState(false);
  const purgeOld = async () => {
    if (!purgeBefore) { toast.error("Pick a cutoff date"); return; }
    if (!window.confirm(`Delete ALL engine history rows with trade_date before ${purgeBefore}? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const tok = localStorage.getItem("pagewise_token");
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/engine-history/purge?before_date=${purgeBefore}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${tok}` } }
      );
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || "Purge failed"); return; }
      toast.success(`Deleted ${j.engine_history_deleted} engine rows + ${j.audit_log_deleted} audit rows`);
      await load();
    } catch (e) {
      toast.error(`Purge failed: ${e.message}`);
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="engine-history-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-display font-semibold text-zinc-950">
            Engine Run History
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            One row per engine pass — when it ran, what it processed, how long it took.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={downloadCsv}
          data-testid="engine-history-export-csv"
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Purge old rows panel */}
      <Card className="p-4 border-rose-200 bg-rose-50/30" data-testid="engine-history-purge-card">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-display font-semibold text-zinc-900">Purge old rows</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Delete engine history entries with trade_date BEFORE the cutoff. Cannot be undone.
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="text-xs text-zinc-500 font-medium">Cutoff date (delete before)</label>
              <input
                type="date"
                value={purgeBefore}
                onChange={(e) => setPurgeBefore(e.target.value)}
                className="mt-1 block border border-zinc-200 rounded-md px-3 py-2 text-sm w-40"
                data-testid="engine-history-purge-date"
              />
            </div>
            <Button
              onClick={purgeOld}
              disabled={purging}
              variant="destructive"
              data-testid="engine-history-purge-btn"
            >
              {purging ? "Purging…" : "Purge Old Rows"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-zinc-500 font-medium">From</label>
            <Input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              data-testid="engine-history-filter-from"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-medium">To</label>
            <Input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              data-testid="engine-history-filter-to"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-medium">Strategy</label>
            <Input
              placeholder="e.g. Large Cap"
              value={filters.strategy_name}
              onChange={(e) => setFilters({ ...filters, strategy_name: e.target.value })}
              data-testid="engine-history-filter-strategy"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-medium">Status</label>
            <Input
              placeholder="success / failed"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              data-testid="engine-history-filter-status"
            />
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Search remarks…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              data-testid="engine-history-filter-search"
            />
            <Button onClick={load} data-testid="engine-history-apply">
              <Search className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setFilters({ from_date: "", to_date: "", strategy_name: "", status: "", search: "" });
                setTimeout(load, 50);
              }}
              data-testid="engine-history-reset"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Runs" value={totals.runs} />
        <Tile label="Total Buys" value={totals.buys} />
        <Tile label="Total Sells" value={totals.sells} />
        <Tile label="Skipped" value={totals.skipped} />
        <Tile label="Time (s)" value={totals.time.toFixed(2)} />
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Run ID</th>
                <th className="px-4 py-3 text-left">Timestamp</th>
                <th className="px-4 py-3 text-left">Trade Date</th>
                <th className="px-4 py-3 text-right">Portfolios</th>
                <th className="px-4 py-3 text-right">Buys</th>
                <th className="px-4 py-3 text-right">Sells</th>
                <th className="px-4 py-3 text-right">Skipped</th>
                <th className="px-4 py-3 text-right">Open Pos.</th>
                <th className="px-4 py-3 text-right">Portfolio Value</th>
                <th className="px-4 py-3 text-right">Time (s)</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-zinc-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-zinc-400">
                    No engine runs match the current filters.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.run_id}
                  className="border-t border-zinc-100 hover:bg-zinc-50"
                  data-testid={`engine-history-row-${r.run_id}`}
                >
                  <td className="px-4 py-2 font-mono text-xs">{r.run_id?.slice(0, 16)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtIST(r.timestamp)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{r.trade_date}</td>
                  <td className="px-4 py-2 text-right">{r.portfolios_processed}</td>
                  <td className="px-4 py-2 text-right text-emerald-700">{r.buys}</td>
                  <td className="px-4 py-2 text-right text-rose-700">{r.sells}</td>
                  <td className="px-4 py-2 text-right text-amber-700">{r.skipped}</td>
                  <td className="px-4 py-2 text-right">{r.open_positions_after}</td>
                  <td className="px-4 py-2 text-right font-mono">{fmtINR(r.portfolio_value_after)}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.processing_time_s?.toFixed(3)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={r.status === "success" ? "default" : "destructive"}>
                      {r.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-display font-semibold text-zinc-950 mt-1">{value}</div>
    </Card>
  );
}
